<template>

    <div id="map-customers"></div>

</template>

<style lang="scss">

    #map-customers {
        height: 100%;
        width: 100%;
        .popup {
            .mapboxgl-popup-content {
                border-radius: 5px;
                padding: 15px;
                text-align: center;
                span {
                    @include font-medium(11px);
                    color: $color-text;
                    display: block;
                    line-height: 1.4em;
                    a {
                        color: $color-primary;
                    }
                }
            }
        }
    }

</style>

<script>

import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {

    props: {
        customers: {
            tyoe: Object,
            required: true,
        },
        show: {
            tyoe: Boolean,
            default: false,
        },
    },

    data() {
        return {
            map: null,
        }
    },

    watch: {

        show(newValue, oldValue) {
            if (newValue === true && !this.map) {
                this.initMap();
                this.map.on('load', () => {
                    this.updateMap();
                    this.fitMap();
                });
            } else if (newValue && !oldValue) {
                this.resetMap();
            }
        },

        customers(val) {
            if (this.show) {
                this.resetMap();
            }
        },

    },

    methods: {

        geojson() {
            return {
                type: 'FeatureCollection',
                features: this.customers
                    .filter(c => {
                        return c.location?.lat && c.location?.lng;
                    })
                    .map(c => {
                        return {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: [c.location.lng, c.location.lat],
                            },
                            properties: {
                                image: c.gravatar,
                                initials: c.initials,
                                title: c.name || 'Unknown Name',
                                description: c.location.region,
                                url: c.link,
                                color: c.color,
                            },
                        }
                    }),
            };
        },

        initMap() {
            // Add map.
            mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;
            this.map = new mapboxgl.Map({
                animate: true,
                attributionControl: false,
                cluster: true,
                container: 'map-customers',
                style: 'mapbox://styles/mapbox/outdoors-v10',
                zoom: 1,
                showZoom: true,
            });

            // Add controls.
            var nav = new mapboxgl.NavigationControl({
                showCompass: false,
            });
            this.map.addControl(nav, 'top-right');

            // Event handling.
            this.map.on('click', 'cluster', (e) => {
                let features = this.map.queryRenderedFeatures(e.point, { layers: ['cluster'] });
                let clusterId = features[0].properties.cluster_id;
                this.map.getSource('customers').getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (!err) {
                        this.map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom });
                    }
                });
            });
            this.map.on('click', 'point', (e) => {
                let props = e.features[0].properties;
                let html = '';
                html += '<span class="title"><a href="' + props.url + '" target="_blank">' + props.title + '</a></span>';
                html += '<span class="desc">' + props.description + '</span>';
                new mapboxgl.Popup({ className: 'popup', closeButton: false })
                    .setLngLat(e.features[0].geometry.coordinates.slice())
                    .setHTML(html)
                    .addTo(this.map);
            });

            // Mouse pointer.
            this.map.on('mouseenter', 'cluster', () => {
                this.map.getCanvas().style.cursor = 'pointer';
            });
            this.map.on('mouseleave', 'cluster', () => {
                this.map.getCanvas().style.cursor = '';
            });
        },

        updateMap() {
            this.map.addSource('customers', {
                type: 'geojson',
                data: this.geojson(),
                cluster: true,
                clusterMaxZoom: 14, // Max zoom to cluster points on.
                clusterRadius: 50 // Radius of each cluster when clustering points.
            });
            this.map.addLayer({
                id: 'cluster',
                type: 'circle',
                source: 'customers',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': '#0032ff',
                    'circle-radius': ['step', ['get', 'point_count'], 25, 10, 30, 20, 35],
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#fff',
                },
            });
            this.map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: 'customers',
                filter: ['has', 'point_count'],
                paint: {
                    'text-color': '#fff',
                },
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': ['Montserrat Bold'],
                    'text-size': 12,
                }
            });
            this.map.addLayer({
                id: 'point',
                type: 'circle',
                source: 'customers',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-color': ['get', 'color'],
                    'circle-radius': 20,
                    'circle-stroke-width': 2,
                    'circle-stroke-color': '#fff',
                },
            });
            this.map.addLayer({
                id: 'point-initials',
                type: 'symbol',
                source: 'customers',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'text-color': '#fff',
                },
                layout: {
                    'text-field': ['get', 'initials'],
                    'text-font': ['Montserrat Bold'],
                    'text-size': 10,
                }
            });
        },

        fitMap() {
            let geojson = this.geojson();
            if (geojson.features.length) {
                let bounds = new mapboxgl.LngLatBounds();
                geojson.features.forEach((feature) => {
                    bounds.extend(feature.geometry.coordinates);
                });
                this.map.fitBounds(bounds, { animate: true });
            } else {
                this.map.setZoom(1);
            }
        },

        resetMap() {
            ['cluster', 'cluster-count', 'point', 'point-initials'].forEach(l => {
                if (this.map.getLayer(l)) this.map.removeLayer(l);
            });
            this.map.removeSource('customers');
            this.updateMap();
            this.fitMap();
        },

    }

}

</script>
